import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';

import { cropWindow } from 'js/src/agent/video/clipFraming';
import type { BoundingBox } from 'js/src/client';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import { getLinkedMediaAssets } from 'js/src/libs/scene-utils';
import { getFaceDetectionsOperation } from 'js/src/luminary/luminary-operations';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { getDrawnAssetBounds } from 'js/src/luminary/luminary-utils';

/** A rectangle in the video's frame, in pixels. */
export interface FrameBox {
    x: number;
    y: number;
    width: number;
    height: number;
}

// Detection boxes the face alone; this much of its size on every side takes in the hair, ears and
// chin a graphic must also keep off.
const HEAD_PADDING = 0.25;
// A detection runs on the backend; past this the graphic is designed from the still alone.
const DETECTION_TIMEOUT_MS = 20000;

/**
 * Where the speaker's head is in the frame, from the backend's face detection at the middle of the
 * scene, mapped through the crop of the scene's talking-head slot. Null when the scene doesn't show a
 * recording or detection fails, so the caller designs from the still alone; empty when the
 * recording shows no faces.
 */
export async function detectSpeakerHeads(
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
    scene: Scene,
): Promise<FrameBox[] | null> {
    const recording = scene.sceneGroup?.mediaAsset?.sourceData;
    const [slot] = getLinkedMediaAssets(scene);
    if (!recording?.width || !recording.height || !slot) {
        return null;
    }
    const { getFaceDetections } = makeOperationsCallable(
        { getFaceDetectionsOperation },
        luminary,
        dispatch,
        getState,
    );
    let faces: BoundingBox[];
    try {
        faces = await withTimeout(getFaceDetections(scene.id), DETECTION_TIMEOUT_MS);
    } catch {
        return null;
    }
    const window = cropWindow(slot, recording);
    const slotBounds = getDrawnAssetBounds(slot);
    return faces.flatMap((face) => {
        const padX = face.width * HEAD_PADDING;
        const padY = face.height * HEAD_PADDING;
        const toSlotX = (x: number) => (x - window.left) / (window.right - window.left);
        const toSlotY = (y: number) => (y - window.top) / (window.bottom - window.top);
        const left = Math.max(0, toSlotX(face.x - padX));
        const right = Math.min(1, toSlotX(face.x + face.width + padX));
        const top = Math.max(0, toSlotY(face.y - padY));
        const bottom = Math.min(1, toSlotY(face.y + face.height + padY));
        if (right <= left || bottom <= top) {
            return [];
        }
        return [
            {
                x: Math.round(slotBounds.x + left * slotBounds.width),
                y: Math.round(slotBounds.y + top * slotBounds.height),
                width: Math.round((right - left) * slotBounds.width),
                height: Math.round((bottom - top) * slotBounds.height),
            },
        ];
    });
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((_resolve, reject) => {
            setTimeout(() => reject(new Error('face detection timed out')), ms);
        }),
    ]);
}
